// src/app/app.config.ts
import { headersInterceptor } from '@core/http/headers.interceptor';
import { offlineInterceptor } from '@core/http/offline.interceptor';
import { ApplicationConfig, ErrorHandler, provideZoneChangeDetection } from '@angular/core';
import { GlobalErrorHandler } from '@core/errors/global-error.handler';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideClientHydration, withEventReplay, withNoIncrementalHydration } from '@angular/platform-browser';
import {
  provideRouter,
  withInMemoryScrolling,
  withPreloading,
  withViewTransitions,
} from '@angular/router';
import { appRoutes } from './app.routes';
import { PreloadStrategy, onViewTransition } from '@core/routing';
import { provideSubscriptionData } from '@domains/subscription/subscription.data.providers';
import { provideOrganizationsData } from '@domains/organizations/organizations.data.providers';
import { provideI18n } from '@core/i18n/i18n.providers';

// Firebase (App/Firestore/Messaging) is registered via root-scoped injection
// tokens in `core/firebase`, not listed here — see FIREBASE_APP et al.
export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      appRoutes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'top', // Always scroll to top on navigation
        anchorScrolling: 'enabled',
      }),
      // Smooth cross-route animation, scoped to /v2 by `onViewTransition`. Auto
      // no-ops on browsers without `document.startViewTransition` and during SSR,
      // so it is safe across every platform and browser.
      withViewTransitions({
        skipInitialTransition: true,
        onViewTransitionCreated: onViewTransition,
      }),
      // Background-preload the /v2 feature chunks (tabs flagged `preload`) so
      // tab switches are instant.
      withPreloading(PreloadStrategy),
    ),
    provideHttpClient(
      withInterceptors([offlineInterceptor, headersInterceptor]),
      withFetch()
    ),
    provideClientHydration(withEventReplay(), withNoIncrementalHydration()),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimationsAsync(),

    // App-wide error handling: stale lazy-chunk auto-recovery + uniform logging.
    { provide: ErrorHandler, useClass: GlobalErrorHandler },

    // i18n at the TRUE root, for the third time the same rule bites (see the two
    // notes below): `TranslationFacade` is `autoProvided: false`, so it exists
    // ONLY where it is registered — and while that was `providePrimaryApp()` (a
    // route-level injector) it was invisible to every root-scoped `@Service()`.
    // `OrgErrorMapper` is one, reached from the root-scoped `OrgContextStore` that
    // gates B2B UI app-wide, so opening `/home` for a member threw NG0201 on
    // `_HomeHubStore -> _OrgContextStore -> _OrgErrorMapper -> _TranslationFacade`.
    //
    // Localized copy is not a feature-tree concern — an error mapper, a toast, a
    // guard or an interceptor can all need it — so the facade belongs here, where
    // BOTH the root and every route injector below can see the one instance.
    // Everything it depends on (I18N_CONFIG · LanguageCacheService · PlatformService
    // · AlertService · LoggerService · HttpClient) is already root-scoped, so
    // nothing moves with it. There is no `/v1` app left in this repo to isolate it
    // from — `/v1/*` is a redirect shim in `app.routes.ts`, nothing more.
    provideI18n(),

    // SubscriptionRepository's config-driven mock/http binding. Must live at the
    // TRUE application root, not inside `providePrimaryApp()` (a route-level
    // injector for `PRIMARY_ROUTES`'s `path: ''`): `SubscriptionStore` is a
    // plain `@Service()` (autoProvided, i.e. `providedIn: 'root'`), so it is
    // constructed against the real root injector and can only see providers
    // registered here — a descendant route injector is invisible to it even
    // though every route is nested under that same tree. Registering it only
    // in `providePrimaryApp()` caused NG0201 the moment anything (e.g.
    // Khoyek's facade) forced `SubscriptionStore` to construct.
    provideSubscriptionData(),
    // Same rule, same reason: `OrgContextStore` is root-scoped because B2B
    // membership gates UI far outside any one route (the Home hub's tab strip,
    // the coverage badge, the commerce surfaces §9.5 switches off), so its
    // repository binding has to be visible to the real root injector.
    provideOrganizationsData(),
  ]
};
